import axios from "axios"
import { type FC, useCallback, useEffect, useState } from "react"
import { Tab, Tabs } from "react-bootstrap"
import LDSEllipsis from "src/components/common/LDS/LDSEllipsis/LDSEllipsis"
import CertificationTable from "src/components/table/CertificationTable/CertificationTable"
import type { EmployeeCertificationElem, EmployeeCertificationSaveModel } from "src/pages/hr/Types/TrainingTypes"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"
import UtilityAuthorization from "./UtilityAuthorization"
import "./Training.css"

interface TrainingProps {
    targetedUserId: number
    readOnly: boolean
}

const Training: FC<TrainingProps> = ({ targetedUserId, readOnly }) => {
    const [certs, setCerts] = useState<EmployeeCertificationElem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const fetchCerts = useCallback(() => {
        setLoading(true)
        setError(false)
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/api/user/GetTrainingCertsByEmployeeId/${targetedUserId}`,
                AxiosConfig.configValue("min")
            )
            .then(res => {
                setCerts(res.data)
            })
            .catch(() => {
                setError(true)
                snackBar({ text: "Failed to load training certifications.", type: "error", timeout: 4000 })
            })
            .finally(() => {
                setLoading(false)
            })
    }, [targetedUserId])

    useEffect(() => {
        fetchCerts()
    }, [fetchCerts])

    const handleSave = async (saveModels: EmployeeCertificationSaveModel[]): Promise<void> => {
        try {
            for (const saveModel of saveModels) {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/user/CreateOrUpdateEmployeeCertificationPost`,
                    saveModel,
                    AxiosConfig.configValue("min")
                )
            }
        } catch {
            snackBar({ text: "Failed to save certification. Please try again.", type: "error", timeout: 4000 })
            fetchCerts()
            throw new Error("Save failed")
        }
        snackBar({ text: "Certifications saved successfully.", type: "success", timeout: 3000 })
    }

    return (
        <div className="TrainingWrapper">
            <Tabs defaultActiveKey="Certifications" variant="tabs" className="TrainingTabs">
                <Tab eventKey="Certifications" title="Certifications">
                    {loading ? (
                        <div className="TrainingStateCenter TrainingTabContent">
                            <LDSEllipsis color="#7f7f7f" />
                        </div>
                    ) : error ? (
                        <div className="TrainingStateCenter TrainingTabContent">
                            <p className="TrainingErrorText">Unable to load training certifications.</p>
                        </div>
                    ) : (
                        <CertificationTable
                            certs={certs}
                            readOnly={readOnly}
                            onSave={handleSave}
                            title="Training Records:"
                        />
                    )}
                </Tab>
                <Tab eventKey="Utilities" title="Utilities" unmountOnExit>
                    <UtilityAuthorization targetedUserId={targetedUserId} readOnly={readOnly} />
                </Tab>
            </Tabs>
        </div>
    )
}

export default Training
